//
// File: AuthProvider.ts
// Description: Creates a singleton react context for Authorization functionality
// This is where the cognito user/auth is exposed to react components
// Copyright: Broadlume 2024
//

import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { createRegisteredContext } from 'react-singleton-context';

import { AuthChallengeCallback, AuthChallengeRoutePaths } from './AuthChallengeCallback';
import { AuthChallengeName } from './AuthChallengeName';
import { User } from './User';

// Use this to track promises for things like loading spinners - https://lemoncode.github.io/react-promise-tracker/docs/getting-started.html
// Simply wrap your promise in trackPromise and it will track it for you
import { useToast } from '@broadlume/willow-ui';
import { trackPromise } from 'react-promise-tracker';
import { QueryClient } from '@tanstack/react-query';
import { setupRequestAuth } from '../api/requestQueue';
import {
  loadJwtClaims,
  loadPermissionsForCompany,
  invalidateAllPermissionsCache,
} from '../api/PermissionAPI';
import { usePermissionStore } from '../store/permissionStore';
import { useI18nTranslation } from '../i18n';

/**
 * System Admin tenant UUID
 * Used when switching to System Admin tenant
 */
export const SYSTEM_ADMIN_TENANT_UUID = '00000000-0000-0000-0000-000000000000' as const;

/**
 * Validates and sanitizes redirect URLs to prevent open redirect attacks.
 * Only allows relative URLs starting with '/'
 * @param redirect - The redirect URL to validate
 * @param defaultRedirect - The default redirect if validation fails (default: '/')
 * @returns A safe redirect URL
 */
function validateRedirectUrl(redirect: string, defaultRedirect: string = '/'): string {
  if (!redirect || typeof redirect !== 'string') {
    return defaultRedirect;
  }

  const trimmed = redirect.trim();

  // Only allow relative URLs starting with '/'
  // Reject absolute URLs, protocol-relative URLs, and javascript: URLs
  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  // Reject any other format (absolute URLs, protocol-relative, etc.)
  return defaultRedirect;
}

/**
 * Type for the singleton AuthContext
 */
export type AuthContextType = {
  /**
   * The current user
   */
  user: User;
  /**
   * Is the user authenticated
   * @returns true if the user is authenticated
   */
  authenticated: () => boolean;
  /**
   * The current admin level of the user
   * @returns The admin level of the user
   */
  adminLevel: () => number;
  /**
   * Resume the current auth session if one exists
   * @returns A promise to resume the current session
   */
  resumeSession: () => Promise<void>;
  /**
   * Log in the user with username and password
   * @param username - The username
   * @param password - The password
   * @param redirect - The redirect path after login
   * @returns A promise to log in the user
   */
  login: (username: string, password: string, redirect: string) => Promise<void>;
  /**
   * Log out the user
   * @returns A promise to log out the user
   */
  logout: () => Promise<void>;
  /**
   * Complete a challenge
   * @param challengeName - The name of the challenge
   * @param challengeParameters - The parameters for the challenge
   * @param redirect - The redirect path after completing the challenge
   * @returns A promise to complete the challenge
   */
  completeChallenge: (
    challengeName: AuthChallengeName,
    challengeParameters: Record<string, string | number | boolean | {}>,
    redirect?: string,
  ) => Promise<void>;
  /**
   * Initiate forgot password flow
   * @param username - The username
   * @returns A promise to initiate the forgot password flow
   */
  forgotPassword: (username: string) => Promise<void>;
  /**
   * Confirm forgot password flow
   * @param username - The username
   * @param newPassword - The new password
   * @param verificationCode - The verification code
   * @returns A promise to confirm the forgot password flow
   */
  confirmForgotPassword: (
    username: string,
    newPassword: string,
    verificationCode: string,
  ) => Promise<void>;
  /**
   * Verify an attribute
   * @param attributeName - The attribute name to verify
   * @param verificationCode - The verification code
   * @returns A promise to verify the attribute
   */
  verifyAttribute: (attributeName: string, verificationCode: string) => Promise<void>;
  /**
   * Get the account type string
   * @returns The account type string
   */
  accountType: () => string;
  /**
   * Switch tenant
   * @param tenantId - The tenant id to switch to
   * @returns A promise to switch tenant
   */
  switchTenant: (tenantId: string) => Promise<void>;
  /**
   * Refresh the current auth session.
   * @param force - When true, always contact Cognito. When false (default),
   *                skip the refresh if the current session is still valid.
   *                Callers that need fresh claims (tenant switch, 401 retry)
   *                must pass true.
   * @returns A promise to refresh the current session
   */
  refreshSession: (force?: boolean) => Promise<void>;
  /**
   * The current tenant id of the user
   * can be used to force a re-render when switching tenants
   */
  currentTenant: string;
  /**
   * NOTE: activeTenant and currentTenant always store the same value
   */
  activeTenant: string;
  /*
   * Set the active tenant
   */
  setActiveTenant: Dispatch<SetStateAction<string>>;
  /**
   * Indicates if the current tenant is System Admin
   * true when currentTenant is SYSTEM_ADMIN_TENANT_UUID, false otherwise
   * false if company switcher has no value
   */
  isTenantSystemAdmin: boolean;
  /**
   * Are we currently switching tenants
   */
  switchingTenant: boolean;
  /**
   * Has the session ended
   */
  sessionEnded: boolean;
  /**
   * End the session
   */
  endSession: () => void;
  /**
   * Is the auth state currently being validated (e.g. silent token refresh in progress)
   * Consumers should treat this as "authenticated" while true to avoid premature redirects
   */
  isValidating: boolean;
  /**
   * Raw boolean auth state — true only when the session is confirmed valid.
   * Unlike authenticated(), this does NOT return true during silent token refresh.
   * Use this when you need to track the actual false→true transition (e.g. login events).
   */
  isAuthenticated: boolean;
};

/**
 * The singleton context for use in the AuthProvider, using the singleton context allows
 * plugin modules to also use the AuthProvider and access the same instance of the AuthContext
 */
const AuthContext = createRegisteredContext<AuthContextType | null>('blx.shell.AuthContext', null);

/**
 * Exposes the AuthContext/Type to the application
 * @param props - AuthProvider props, children, sessionUser, challengePaths
 * @returns AuthContext React Provider component
 */
export const AuthProvider = ({
  children,
  sessionUser,
  challengePaths,
  queryClient,
}: {
  children: ReactNode;
  sessionUser: User;
  challengePaths: AuthChallengeRoutePaths;
  queryClient?: QueryClient;
}) => {
  const [user, setUser] = useState(sessionUser);

  const [sessionEnded, setSessionEnded] = useState<boolean>(false); // Keep track of if the session has ended - initially true if the user is not authenticated
  const [activeTenant, setActiveTenant] = useState<string>(user.attributes.tenant_id); // Keep track of the current tenant id - used to force a re-render when switching tenants
  const [switchingTenant, setSwitchingTenant] = useState<boolean>(false); // Keep track of if we are currently switching tenants
  // Tracks authenticated status as React state so consumers re-render on changes
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(user.isAuthenticated);
  // If the session's access token is already expired at mount but a refresh
  // token is still on hand, assume we can recover. Start in "validating" state
  // so AuthenticatedRoute doesn't flash a redirect to /login before the
  // mount-time refresh (below) has a chance to run.
  const needsInitialRefresh = !user.isAuthenticated && user.hasRefreshToken;
  // True while a silent token refresh is in-flight — prevents premature login redirects
  const [isValidating, setIsValidating] = useState<boolean>(needsInitialRefresh);
  const navigate = useNavigate();
  const { t } = useI18nTranslation();
  const { toast } = useToast();
  const logoutHandledRef = useRef<boolean>(false); // Track if logout event has been handled to prevent duplicate redirects
  const logoutCleanupTimeoutRef = useRef<number | null>(null); // Track timeout for logout event cleanup
  const storageEventTimeoutRef = useRef<number | null>(null); // Track timeout for storage event redirects
  const isAuthenticatedRef = useRef<boolean>(user.isAuthenticated); // Track current auth state for event handlers
  const isValidatingRef = useRef<boolean>(needsInitialRefresh); // Ref mirror for isValidating — safe in async callbacks

  // Keep activeTenant in sync with React state — used by 401 refresh callback to avoid stale closure capture.
  const activeTenantRef = useRef(activeTenant);
  useEffect(() => {
    activeTenantRef.current = activeTenant;
  }, [activeTenant]);

  // Keep isAuthenticatedRef in sync with React state
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Register auth callbacks for the HTTP request queue.
  // The queue uses these to refresh the token on 401 and to end the session
  // when the refresh token is also expired.
  useEffect(() => {
    setupRequestAuth(
      // Always returns the latest access token (idToken getter is live)
      () => user.idToken,
      // Refresh: call Cognito refresh and sync React state.
      // Force: a 401 means the server rejected the token, so the client-side
      // isValid() check (clock skew, revocation) can't be trusted here.
      async () => {
        await user.refreshSession(true);
        setIsAuthenticated(user.isAuthenticated);
        isAuthenticatedRef.current = user.isAuthenticated;

        // Imperatively update Zustand permission store with new JWT claims + company permissions.
        if (user.idToken) {
          const currentCompany = activeTenantRef.current;
          loadJwtClaims(user.idToken, currentCompany);
          if (currentCompany && currentCompany !== SYSTEM_ADMIN_TENANT_UUID) {
            void loadPermissionsForCompany({ companyId: currentCompany, user }).catch(() => {
              /* non-critical — permissions may already be cached */
            });
          }
        }
      },
      // Session end: token is truly expired — let AuthenticatedRoute redirect to login
      () => {
        setIsAuthenticated(false);
        isAuthenticatedRef.current = false;
        void user.signOut().catch((err) => {
          console.warn('[Auth] Failed to clear local session after refresh-token expiry:', err);
        });
      },
    );
  }, []); // user is stable for the lifetime of the provider

  const authenticated = () => {
    // Return true while a silent refresh is underway so guards don't redirect
    if (isValidatingRef.current) return true;
    return isAuthenticated;
  };

  const adminLevel = () => {
    return user.attributes.adminLevel;
  };

  // Perform a forced refresh and sync React state to the outcome. Callers
  // own the pre-flight concurrency guard and the `isValidating` flip to true;
  // this helper owns only the settle-time state updates so both the periodic
  // silentRefresh and the mount-time recovery effect share one codepath.
  // Force: callers reach this helper specifically because we believe the
  // current session is stale — don't let isValid() short-circuit us.
  const performRefreshAndSync = useCallback(async () => {
    try {
      await user.refreshSession(true);
      setIsAuthenticated(true);
      isAuthenticatedRef.current = true;
    } catch (err) {
      console.warn('[Auth] Silent token refresh failed — refresh token may be expired:', err);
      setIsAuthenticated(false);
      isAuthenticatedRef.current = false;
    } finally {
      isValidatingRef.current = false;
      setIsValidating(false);
    }
  }, []);

  const silentRefresh = useCallback(async () => {
    if (isValidatingRef.current) return; // Prevent concurrent calls
    isValidatingRef.current = true;
    setIsValidating(true);
    await performRefreshAndSync();
  }, [performRefreshAndSync]);

  // Mount-time recovery: if we loaded with an expired access token but a
  // usable refresh token, try to refresh before routing decisions run.
  // isValidating is already seeded to true (lazy init above), so guards
  // pass on the first render while this is in flight.
  useEffect(() => {
    if (!needsInitialRefresh) return;
    performRefreshAndSync();
    // Run exactly once on mount; `needsInitialRefresh` is a render-time snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consolidated mount-time + tenant-switch permission loading:
  // Loads JWT claims + company permissions when authenticated, and reloads
  // whenever activeTenant changes (tenant switch). This replaces the two separate
  // effects below to avoid duplicate loading and race conditions.
  useEffect(() => {
    if (!user.isAuthenticated || !user.idToken) return;

    const companyId = activeTenant;

    loadJwtClaims(user.idToken, companyId);
    void loadPermissionsForCompany({ companyId, user }).catch((err) => {
      console.warn('[Auth] Failed to load permissions:', err);
    });
  }, [user.isAuthenticated, user.idToken, activeTenant]);

  // Monitor token validity every minute and on tab focus.
  // When the access token expires but a refresh token is available, trigger a silent refresh.
  useEffect(() => {
    const checkAndRefresh = () => {
      // Only attempt if user was authenticated but the access token has now expired
      if (!user.isAuthenticated && isAuthenticatedRef.current && user.hasRefreshToken) {
        silentRefresh();
      }
    };

    const interval = setInterval(checkAndRefresh, 60_000);

    const onVisibilityChange = () => {
      if (!document.hidden) checkAndRefresh();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [silentRefresh]);

  // try to resume a session if one exists from stored credentials
  const resumeSession = async () => {
    await trackPromise(
      user.resumeSession().catch((err) => {
        throw err;
      }),
    );
    setIsAuthenticated(user.isAuthenticated);
    isAuthenticatedRef.current = user.isAuthenticated;

    // Load JWT claims and company permissions after session resume.
    if (user.idToken) {
      const currentCompany = user.attributes.tenant_id;
      loadJwtClaims(user.idToken, currentCompany);
      if (currentCompany && currentCompany !== SYSTEM_ADMIN_TENANT_UUID) {
        void loadPermissionsForCompany({ companyId: currentCompany, user }).catch(() => {
          /* non-critical — permissions may already be cached */
        });
      }
    }
  };

  // refresh the current session
  const refreshSession = async (force = false) => {
    await trackPromise(
      user.refreshSession(force).catch((err) => {
        throw err;
      }),
    );
    setIsAuthenticated(user.isAuthenticated);
  };

  // log in user with username and password
  const login = async (username: string, password: string, redirect: string) => {
    await trackPromise(
      user.signIn(username, password).catch((err) => {
        throw err;
      }),
    );
    if (user.isAuthenticated) {
      setIsAuthenticated(true);
      isAuthenticatedRef.current = true; // Update auth ref
      sessionStorage.removeItem('blx_signing_out'); // Clear sign-out guard set by logout()

      // Clear all React Query caches to ensure fresh data after login
      // This prevents stale cached queries from being used post-login
      queryClient?.clear();

      // Clear the permission loading cache to force fresh API calls
      invalidateAllPermissionsCache();

      const safeRedirect = validateRedirectUrl(redirect);
      navigate(safeRedirect);
      toast({
        title: t('shared.auth.session_toasts.login_title', { defaultValue: 'Login' }),
        description: t('shared.auth.session_toasts.login_success_description', {
          defaultValue: 'Logged in successfully',
        }),
        variant: 'success',
      });

      // Load JWT claims and company permissions after successful login.
      // This now triggers fresh API calls because we cleared the cache above.
      if (user.idToken) {
        const currentCompany = user.attributes.tenant_id;
        loadJwtClaims(user.idToken, currentCompany);
        if (currentCompany && currentCompany !== SYSTEM_ADMIN_TENANT_UUID) {
          void loadPermissionsForCompany({ companyId: currentCompany, user }).catch(() => {
            /* non-critical — permissions may already be cached */
          });
        }
      }

      navigate(safeRedirect);
    }
  };

  // log out user
  const logout = async () => {
    // Set flag before clearing React auth state so UnauthenticatedRoute doesn't
    // redirect back to home while signOut() is still completing (async).
    sessionStorage.setItem('blx_signing_out', '1');
    setIsAuthenticated(false);
    isAuthenticatedRef.current = false; // Update auth ref

    // Reset the Zustand permission store on logout.
    usePermissionStore.getState().resetAll();
    // Clear the deduplication cache so stale data doesn't linger across sessions.
    invalidateAllPermissionsCache();

    // Clear all React Query caches to prevent stale authenticated API calls.
    // This ensures no cached queries (companies/listing, plugins/sidebar, permissions, etc.)
    // persist after logout and trigger unwanted API calls.
    // Using clear() is more aggressive than invalidateQueries(), but necessary on logout
    // to ensure a clean state for the next session.
    queryClient?.clear();

    // Notify other tabs about logout before clearing storage
    localStorage.setItem('blx_logout_event', Date.now().toString());

    try {
      await trackPromise(
        user.signOut().catch((err) => {
          throw err;
        }),
      );
    } finally {
      // Always clear the signing out flag, even if signOut fails or is interrupted
      sessionStorage.removeItem('blx_signing_out');
    }

    // Clear popup data on logout
    sessionStorage.removeItem('displayedInviteCompaniesPopup');
    sessionStorage.removeItem('invitationFlowTriggered');
    sessionStorage.removeItem('homebaseLocationVerificationPromptShown');

    // Clear any existing cleanup timeout
    if (logoutCleanupTimeoutRef.current !== null) {
      clearTimeout(logoutCleanupTimeoutRef.current);
    }

    // Clean up logout event flag after a short delay
    logoutCleanupTimeoutRef.current = window.setTimeout(() => {
      localStorage.removeItem('blx_logout_event');
      logoutCleanupTimeoutRef.current = null;
    }, 1000);

    navigate('/login', { replace: true });
    toast({
      title: t('shared.auth.session_toasts.logout_title', { defaultValue: 'Logout' }),
      description: t('shared.auth.session_toasts.logout_success_description', {
        defaultValue: 'Logged out successfully',
      }),
      variant: 'success',
    });
  };

  // request switch tenant
  const switchTenant = async (tenantId: string) => {
    setSwitchingTenant(true);
    await trackPromise(
      user.switchTenant(tenantId).catch((err) => {
        throw err;
      }),
    );
    // Force: the session's tokens are still valid, but we need new ones that
    // carry the updated tenant claim from the ID token.
    await trackPromise(refreshSession(true));

    // Imperatively update Zustand permission store for the new company.
    const oldCompany = activeTenant;
    setActiveTenant(user.attributes.tenant_id); // Tenant has changed, force a re-render of applicable components. Note: activeTenant and currentTenant both reflect this change as they reference the same value.

    if (user.idToken) {
      const newCompanyId = user.attributes.tenant_id;
      loadJwtClaims(user.idToken, newCompanyId);
    }
    // Load permissions for the NEW company regardless of old state —
    // handles case where oldCompany was empty string (Cognito attributes not yet populated).
    const newCompanyId = user.attributes.tenant_id;
    if (newCompanyId && newCompanyId !== SYSTEM_ADMIN_TENANT_UUID) {
      if (oldCompany) {
        usePermissionStore.getState().clearFeaturesForCompany(oldCompany);
      }
      // Clear dedup cache for the old company so it doesn't block fresh loads later.
      invalidateAllPermissionsCache();
      void loadPermissionsForCompany({ companyId: newCompanyId, user }).catch(() => {
        /* non-critical — permissions may already be cached */
      });
    }

    setSwitchingTenant(false);
  };

  //Complete password challenge and set required attributes
  const completeChallenge = async (
    challengeName: AuthChallengeName,
    challengeParameters: Record<string, string | number | boolean | {}>,
    redirect = '/',
  ) => {
    // new password required challenge
    if (challengeName === AuthChallengeName.NEW_PASSWORD_REQUIRED) {
      await trackPromise(
        user
          .completeNewPasswordChallenge(
            challengeParameters['password'] as string,
            challengeParameters['attributes'] || {},
            challengeParameters['fullFormatPhone'] as Record<string, string> | undefined,
          )
          .catch((err) => {
            throw err;
          }),
      );

      if (user.isAuthenticated) {
        setIsAuthenticated(true);
        isAuthenticatedRef.current = true;
        sessionStorage.removeItem('blx_signing_out');

        // Clear caches so the first post-challenge render fetches fresh data,
        // mirroring the post-login sync in login() — otherwise permissions
        // only load after a manual page refresh triggers resumeSession().
        queryClient?.clear();
        invalidateAllPermissionsCache();

        if (user.idToken) {
          const currentCompany = user.attributes.tenant_id;
          loadJwtClaims(user.idToken, currentCompany);
          if (currentCompany && currentCompany !== SYSTEM_ADMIN_TENANT_UUID) {
            void loadPermissionsForCompany({ companyId: currentCompany, user }).catch(() => {
              /* non-critical — permissions may already be cached */
            });
          }
        }
      }
    }
    // More challenge types can be added here..
    const safeRedirect = validateRedirectUrl(redirect);
    navigate(safeRedirect, { replace: true });
  };

  // Initiate forgot password flow
  const forgotPassword = async (username: string) => {
    await trackPromise(
      user.forgotPassword(username).catch((err) => {
        throw err;
      }),
    );
  };

  // Confirm forgot password flow
  const confirmForgotPassword = async (
    username: string,
    newPassword: string,
    verificationCode: string,
  ) => {
    await trackPromise(
      user.confirmForgotPassword(username, newPassword, verificationCode).catch((err) => {
        throw err;
      }),
    );
  };

  // Verify an attribute
  const verifyAttribute = async (attributeName: string, verificationCode: string) => {
    await trackPromise(
      user.veryfyAttribute(attributeName, verificationCode).catch((err) => {
        throw err;
      }),
    );
  };

  // get account type string
  const accountType = () => {
    switch (user.attributes.adminLevel) {
      case 0:
        return '';
      case 1:
        return 'Company Admin';
      case 2:
        return 'Broadlume Admin';
      case 3:
        return 'Broadlume Super Admin';
      default:
        return '';
    }
  };

  // setup our auth challenge callback, handles navigating to
  // the correct page when the user login is challenged
  const authChallengeCallback = {
    // invoked when password change is required, redirects to the change password page
    newPasswordRequired(
      userAttributes: Record<string, string>,
      requiredAttributes: Record<string, string>,
    ): void {
      navigate(challengePaths.newPasswordRequired, {
        state: { userAttributes, requiredAttributes },
      });
    },
    enterVerificationCode(attributeName: string): void {
      navigate(challengePaths.enterVerificationCode, { state: { attName: attributeName } });
    },
  } as AuthChallengeCallback;

  // inital mount, setup our auth challenge callback
  useEffect(() => {
    user.authChallengeCallback = authChallengeCallback;
    // Email is always verified at account creation (AdminCreateUser sets
    // email_verified=true), so no auto-triggered verification code is needed here.
  }, []);

  // Listen for storage events from other tabs (e.g., logout in another tab)
  useEffect(() => {
    // Helper function to handle session invalidation with redirect
    const handleSessionInvalidation = (reason: string) => {
      console.log(reason);
      logoutHandledRef.current = true;
      isAuthenticatedRef.current = false;
      setSessionEnded(true);

      // Clear any existing redirect timeout
      if (storageEventTimeoutRef.current !== null) {
        clearTimeout(storageEventTimeoutRef.current);
      }

      // Small delay to show alert before hard navigation to login
      storageEventTimeoutRef.current = window.setTimeout(() => {
        window.location.href = '/login';
        storageEventTimeoutRef.current = null;
      }, 1000);
    };

    const handleStorageChange = (event: StorageEvent) => {
      // Prevent duplicate handling - if logout has been handled, ignore all subsequent storage events
      if (logoutHandledRef.current) {
        return;
      }

      // Check if logout occurred in another tab
      if (event.key === 'blx_logout_event' && event.newValue) {
        handleSessionInvalidation('Logout detected in another tab');
        return; // Early return to prevent processing the subsequent localStorage.clear() event
      }

      // Check if localStorage was cleared (session invalidated)
      // Only process if not already handled by logout event above
      if (event.key === null && isAuthenticatedRef.current) {
        handleSessionInvalidation('Storage cleared, session invalidated');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);

      // Clear any pending timeouts
      if (storageEventTimeoutRef.current !== null) {
        clearTimeout(storageEventTimeoutRef.current);
        storageEventTimeoutRef.current = null;
      }
      if (logoutCleanupTimeoutRef.current !== null) {
        clearTimeout(logoutCleanupTimeoutRef.current);
        logoutCleanupTimeoutRef.current = null;
      }

      // Reset the ref when component unmounts
      logoutHandledRef.current = false;
    };
  }, []); // Removed navigate and user.isAuthenticated from dependencies since we use refs

  // Calculate isTenantSystemAdmin based on currentTenant
  // true when currentTenant is SYSTEM_ADMIN_TENANT_UUID, false otherwise
  // false if tenant is empty/null (company switcher has no value)
  const isTenantSystemAdmin = activeTenant === SYSTEM_ADMIN_TENANT_UUID;

  return (
    <AuthContext.Provider
      value={{
        user,
        authenticated,
        adminLevel,
        resumeSession,
        login,
        logout,
        completeChallenge,
        forgotPassword,
        confirmForgotPassword,
        verifyAttribute,
        accountType,
        switchTenant,
        refreshSession,
        currentTenant: activeTenant,
        activeTenant: activeTenant,
        setActiveTenant: setActiveTenant,
        isTenantSystemAdmin,
        switchingTenant: switchingTenant,
        sessionEnded: sessionEnded,
        endSession: () => setSessionEnded(true),
        isValidating,
        isAuthenticated,
      }}
    >
      {children as React.ReactNode}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access the AuthContext
 * @returns The AuthContext
 */
export const useAuth = () => {
  return useContext(AuthContext as React.Context<AuthContextType | null>);
};
